import React, { RefObject } from 'react';
import { Box, TextV2, CheckboxV2, Flex } from '@withjoy/joykit';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { Tabs, TabItemProps } from '@shared/components/Tabs/Tabs';
import { FormikProps } from 'formik';
import { StyledNumericFormControl, StyledNumericInput, StyledInputPrefix, StyledHelpIcon } from '@apps/registry/common/components/AddCashFund/AddCashFundDialog.styles';
import { AddCashFundDialogFields } from '@apps/registry/common/components/AddCashFund/types';
import { CurrencyInput } from './AutoGrowInput';

interface CashFundAmountProps {
  formik: FormikProps<AddCashFundDialogFields>;
  isAnyAmountSelected: boolean;
  amountTabs: TabItemProps[];
  isEditDialog?: boolean;
  isMobile: boolean;
  onQtyChange: React.ChangeEventHandler<HTMLInputElement>;
  onQtyFocus: React.ChangeEventHandler<HTMLInputElement>;
  preventNonNumericalInput: React.KeyboardEventHandler<HTMLInputElement>;
  onAmountChange: React.ChangeEventHandler<HTMLInputElement>;
  onAmountFocus: React.ChangeEventHandler<HTMLInputElement>;
  amountRef?: RefObject<HTMLSpanElement>;
  amountPrefixOffset?: number;
  totalRef?: RefObject<HTMLSpanElement>;
  totalGoal: string | number;
  currency: string;
  onHideGoalToggle: () => void;
  isGoalHidden: boolean;
  onHelpIconClick: () => void;
}

export const CashFundAmount: React.FC<CashFundAmountProps> = props => {
  const {
    formik,
    isMobile,
    isAnyAmountSelected,
    amountTabs,
    isEditDialog,
    onQtyChange,
    onQtyFocus,
    preventNonNumericalInput,
    onAmountChange,
    onAmountFocus,
    amountRef,
    amountPrefixOffset,
    totalRef,
    totalGoal,
    currency,
    onHideGoalToggle,
    isGoalHidden,
    onHelpIconClick,
  } = props;
  const errors = formik.errors;
  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { goalStepLabel, quantityLabel, amountLabel, totalGoalLabel, hideGoalLabel, noGoalCaption } = getCashFundDialogTranslations();
  const renderAmountTabsContent = () => {
    if (!isAnyAmountSelected) {
      if (isEditDialog) {
        return (
          <Flex
            columnGap={4}
            rowGap={4}
            marginBottom={4}
            flexDirection={['column', 'column', 'row']}
          >
            <StyledNumericFormControl
              label={
                <TextV2
                  as="span"
                  typographyVariant="label3"
                >
                  {quantityLabel}
                </TextV2>
              }
              isMobile={isMobile}
              isEditDialog
              isRequired
              error={errors.qty}
              isInvalid={formik.touched.qty && !!errors.qty}
            >
              <StyledNumericInput
                {...formik.getFieldProps('qty')}
                type="number"
                width="70px"
                inputMode="numeric"
                pattern="[0-9]*"
                onKeyDown={preventNonNumericalInput}
                tabIndex={0}
                onChange={onQtyChange}
                onFocus={onQtyFocus}
              />
            </StyledNumericFormControl>

            <StyledNumericFormControl
              label={
                <TextV2
                  as="span"
                  typographyVariant="label3"
                >
                  {amountLabel}
                </TextV2>
              }
              isRequired
              error={errors.amount}
              isMobile={isMobile}
              isEditDialog
              isCustomPadding
              isInvalid={formik.touched.amount && !!errors.amount}
            >
              <Flex
                alignItems="center"
                overflow="hidden"
                position="relative"
              >
                <StyledNumericInput
                  {...formik.getFieldProps('amount')}
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onKeyDown={preventNonNumericalInput}
                  tabIndex={0}
                  onChange={onAmountChange}
                  onFocus={onAmountFocus}
                />
                <StyledInputPrefix
                  isFixedPrefix
                  prefixOffset={16}
                >
                  {currency}
                </StyledInputPrefix>
              </Flex>
            </StyledNumericFormControl>

            <StyledNumericFormControl
              label={
                <TextV2
                  as="span"
                  typographyVariant="hed2"
                >
                  {totalGoalLabel}
                </TextV2>
              }
              isEditDialog
              width={['100%', '100%', '128px']}
            >
              <Flex
                height="48px"
                alignItems="center"
                overflow="hidden"
                position="relative"
                rowGap={4}
              >
                <StyledInputPrefix
                  isFixedPrefix
                  prefixOffset={0}
                >
                  {currency}
                </StyledInputPrefix>
                <TextV2 paddingLeft="16px">{totalGoal}</TextV2>
              </Flex>
            </StyledNumericFormControl>
          </Flex>
        );
      } else {
        return (
          <Flex
            width="100%"
            alignItems="flex-start"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <StyledNumericFormControl
              marginLeft={[0, null, 7]}
              label={
                <TextV2
                  as="span"
                  typographyVariant="label3"
                >
                  {quantityLabel}
                </TextV2>
              }
              error={formik.touched.qty && errors.qty ? errors.qty : undefined}
              isInvalid={formik.touched.qty && !!errors.qty}
              isRequired
              display="flex"
              flexDirection="column"
              alignItems="center"
              width={['50%', 'fit-content']}
              margin={['auto', '0px']}
            >
              <StyledNumericInput
                {...formik.getFieldProps('qty')}
                type="number"
                width="82px"
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={onQtyChange}
                onFocus={onQtyFocus}
                onKeyDown={preventNonNumericalInput}
                tabIndex={0}
              />
            </StyledNumericFormControl>
            <StyledNumericFormControl
              label={
                <TextV2
                  as="span"
                  typographyVariant="label3"
                >
                  {amountLabel}
                </TextV2>
              }
              error={formik.touched.amount && errors.amount ? errors.amount : undefined}
              isInvalid={formik.touched.amount && !!errors.amount}
              isRequired
              display="flex"
              flexDirection="column"
              alignItems="center"
              width={['50%', 'fit-content']}
            >
              <Flex
                alignItems="center"
                overflow="hidden"
                position="relative"
              >
                <span
                  style={{ visibility: 'hidden', zIndex: -1, position: 'absolute' }}
                  ref={amountRef}
                >
                  {totalGoal}
                </span>
                <StyledNumericInput
                  {...formik.getFieldProps('amount')}
                  type="number"
                  width="128px"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={onAmountChange}
                  onFocus={onAmountFocus}
                  onKeyDown={preventNonNumericalInput}
                  tabIndex={0}
                />
                <StyledInputPrefix prefixOffset={amountPrefixOffset ? amountPrefixOffset : 0}> {currency} </StyledInputPrefix>
              </Flex>
            </StyledNumericFormControl>
            <StyledNumericFormControl
              label={
                <TextV2
                  as="span"
                  typographyVariant="label3"
                >
                  {totalGoalLabel}
                </TextV2>
              }
              textAlign="center"
              alignItems="center"
              width={['100%', 'fit-content']}
              marginTop={['32px', '0px']}
            >
              <Flex
                alignItems="center"
                overflow="hidden"
                position="relative"
                columnGap="48px"
                margin-left="55px"
                height="50px"
                width={['100%', '100%', 'fit-content']}
              >
                <span
                  style={{ visibility: 'hidden', zIndex: -1, position: 'absolute' }}
                  ref={totalRef}
                >
                  {totalGoal}
                </span>
                <TextV2
                  margin="auto"
                  width="fit-content"
                >
                  {currency} {totalGoal}
                </TextV2>
              </Flex>
            </StyledNumericFormControl>
          </Flex>
        );
      }
    } else {
      return (
        <Flex
          flexDirection="column"
          alignItems={isEditDialog ? 'flex-start' : 'center'}
          width="100%"
        >
          <StyledNumericFormControl
            label={
              <TextV2
                as="span"
                typographyVariant="label3"
              >
                {totalGoalLabel}
              </TextV2>
            }
            error={formik.touched.amount && errors.amount ? errors.amount : undefined}
            isInvalid={formik.touched.amount && !!errors.amount}
            isRequired
            isMobile={isMobile}
            isEditDialog={isEditDialog}
            isCustomPadding={isEditDialog}
            width="100%"
            marginBottom={isEditDialog && !isMobile ? 0 : isMobile ? 4 : 6}
          >
            <Flex
              flexDirection={isEditDialog && !isMobile ? 'row' : 'column'}
              width="100%"
              justifyContent="space-between"
              alignItems={isEditDialog && isMobile ? 'flex-start' : 'center'}
            >
              {/* New component */}
              <CurrencyInput
                currency={currency}
                hasError={formik.touched.amount && !!errors.amount}
                inputProps={{
                  ...formik.getFieldProps('amount'),
                  onChange: onAmountChange,
                  onFocus: onAmountFocus,
                  onKeyDown: preventNonNumericalInput,
                }}
              />
              <CheckboxV2
                typographyVariant="body2"
                value="hideFundingGoal"
                isChecked={isGoalHidden}
                onChange={onHideGoalToggle}
                display="flex"
                marginTop={isEditDialog ? ['24px', '24px', 'unset'] : '24px'}
              >
                {hideGoalLabel}
              </CheckboxV2>
            </Flex>
          </StyledNumericFormControl>
          <TextV2
            typographyVariant="label2"
            color="mono14"
            marginY={3}
          >
            {noGoalCaption}
          </TextV2>
        </Flex>
      );
    }
  };

  return (
    <Flex
      flexDirection="column"
      alignItems={isEditDialog ? 'flex-start' : 'center'}
      width="100%"
    >
      <Flex
        position="relative"
        flexDirection="column"
        alignItems={isEditDialog ? 'flex-start' : 'center'}
        width={isEditDialog ? '100%' : '330px'}
        marginBottom={5}
      >
        <Flex
          alignItems="center"
          marginBottom={3}
        >
          <TextV2 typographyVariant="hed2">{goalStepLabel}</TextV2>
          <StyledHelpIcon onClick={onHelpIconClick} />
        </Flex>
        <Tabs
          tabTextProps={{
            typographyVariant: 'body3',
          }}
          items={amountTabs}
          isMobile={isMobile}
          width={isEditDialog ? '100%' : '330px'}
          initialIndicatorWidth={isEditDialog ? '48%' : undefined}
        />
      </Flex>
      <Box width="100%">{renderAmountTabsContent()}</Box>
    </Flex>
  );
};
